import React, { useState } from "react";
import { Box, IconButton, Typography, styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  StewartDialog,
  StewartDialogActions,
} from "controls/global/dialogs/stewart-dialog/StewartDialog";
import ActionButton from "controls/global/action-button";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gradients,
  iconSize,
  padding,
  gaps,
} from "theme/defaultStyle";

import { RemoveRedEye } from "@mui/icons-material";
import ViewAllSpecialRisksTable from "./ViewAllSpecialRisksTable/ViewAllSpecialRisksTable";
import { getBlankRow, SelectedSpecialRiskType } from "./SpecialRisks";

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxWidth: "800px",
    minWidth: "800px",
    maxHeight: "calc(100vh - 100px)",
    height: "auto",
    paddingTop: padding.xlarge21,
    paddingRight: padding.xlarge22,
    paddingBottom: padding.zero,
    paddingLeft: padding.xlarge22,
    overflowX: "hidden",
    overflowY: "hidden",
    display: "flex",
    flexDirection: "column",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.large1,
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const IconContainer = styled(Box)({
  background: gradients.blueGradient08,
  borderRadius: borderRadius.large,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const CloseIconContainer = styled(Box)({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: "auto",
  width: "32px",
  height: "32px",
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
  },
  "& .iconButton": {
    padding: padding.zero,
  },
});

const StyledDialogAction = styled(StewartDialogActions)({
  justifyContent: "right",
  padding: `${padding.large1} ${padding.zero} ${padding.xlarge22}`,
  flexShrink: 0,
});

const TableContainer = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
});

type ViewAllSpecialRisksDialogProps = {
  isOpen: boolean;
  specialRiskItems: any[];
  isNoSpecialRisksEnabled: boolean;
  selectedSpecialRisks: SelectedSpecialRiskType[];
  setSelectedSpecialRisks: (specialRisks: SelectedSpecialRiskType[]) => void;
  updateFormValues: (risks: SelectedSpecialRiskType[]) => void;
  onClose: () => void;
};

const ViewAllSpecialRisksDialog = ({
  isOpen,
  specialRiskItems = [],
  isNoSpecialRisksEnabled = false,
  selectedSpecialRisks,
  setSelectedSpecialRisks,
  updateFormValues,
  onClose,
}: ViewAllSpecialRisksDialogProps) => {
  const [tableSelectedSpecialRisks, setTableSelectedSpecialRisks] = useState<
    SelectedSpecialRiskType[]
  >([...selectedSpecialRisks.filter((risk) => risk.selectedSpecialRisk)]);

  const handleSubmit = () => {
    const updatedSpecialRisks =
      tableSelectedSpecialRisks?.length &&
      tableSelectedSpecialRisks?.length === specialRiskItems.length
        ? [...tableSelectedSpecialRisks]
        : tableSelectedSpecialRisks?.length
        ? [...tableSelectedSpecialRisks, getBlankRow()]
        : [getBlankRow()];
    setSelectedSpecialRisks(updatedSpecialRisks);

    updateFormValues(updatedSpecialRisks);
    onClose();
  };

  return (
    <StyledDialog open={isOpen} onClose={onClose}>
      <StyledDialogTitle>
        <IconContainer>
          <RemoveRedEye />
        </IconContainer>
        <Typography>View All Special Risks</Typography>
        <CloseIconContainer>
          <IconButton className="iconButton" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </CloseIconContainer>
      </StyledDialogTitle>

      <TableContainer mt={4} display="flex" flexDirection="column" gap={3}>
        <ViewAllSpecialRisksTable
          specialRiskItems={specialRiskItems}
          tableSelectedSpecialRisks={tableSelectedSpecialRisks}
          isNoSpecialRisksEnabled={isNoSpecialRisksEnabled}
          setTableSelectedSpecialRisks={setTableSelectedSpecialRisks}
        />
      </TableContainer>

      <StyledDialogAction>
        {isNoSpecialRisksEnabled ? (
          <ActionButton
            variant="contained"
            color="primary"
            onClick={() => onClose()}
            disabled={false}
          >
            Done
          </ActionButton>
        ) : (
          <>
            <ActionButton
              variant="outlined"
              color="secondary"
              onClick={() => onClose()}
              disabled={false}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="contained"
              color="primary"
              onClick={() => handleSubmit()}
              disabled={false}
            >
              Save
            </ActionButton>
          </>
        )}
      </StyledDialogAction>
    </StyledDialog>
  );
};

export default ViewAllSpecialRisksDialog;
